import { Badge, Box, Group, Stack, Text } from '@mantine/core'
import { PriorityBadge } from '../PriorityBadge'
import { ProtocolNumberBadge } from '../ProtocolNumberBadge'
import { rotuloDoTipoDeChamado } from '../tipoDeChamado'
import { rotuloDoAtendimento } from '../AtendidoPor'
import type { LadoAtendimento } from '../DestinoDeAtendimento'

/** O que o bloco do remetente lê do chamado — tudo já vem de `/v1/chamados/{id}`. */
export interface ChamadoDoRemetente {
  requested_by_name: string
  origin?: string | null
  protocol_number: string | null
  request_type: string
  priority: string
  category: { name: string; sla_resolution_hours: number | null } | null
  assignee: { full_name: string } | null
  created_at: string
  resolved_at: string | null
  closed_at: string | null
  target_template_name?: string | null
  lados?: {
    solicitante: { tenant_id: number; tenant_nome: string | null; org_nome: string | null } | null
    atendimento: LadoAtendimento | null
  } | null
}

export function iniciaisDe(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean)
  if (partes.length === 0) return '?'
  const [a, b] = [partes[0], partes.length > 1 ? partes[partes.length - 1] : '']
  return (a[0] + (b[0] ?? '')).toUpperCase()
}

export function formatarData(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

const UNIDADES: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 3600], ['month', 30 * 24 * 3600], ['day', 24 * 3600],
  ['hour', 3600], ['minute', 60],
]

/** "há 3 meses", "há 2 horas": a idade do chamado, na maior unidade inteira. */
export function idadeDe(iso: string, agora: Date = new Date()): string {
  const segundos = Math.max(0, (agora.getTime() - new Date(iso).getTime()) / 1000)
  const rtf = new Intl.RelativeTimeFormat('pt-BR', { numeric: 'auto' })
  for (const [unidade, s] of UNIDADES) {
    if (segundos >= s) return rtf.format(-Math.floor(segundos / s), unidade)
  }
  return 'agora'
}

/** "para Organização · fila X", o "to me" do modelo; sem atendimento resolvido, o time. */
export function destinoDoChamado(c: Pick<ChamadoDoRemetente, 'lados' | 'target_template_name'>): string {
  const at = c.lados?.atendimento
  if (at) return `para ${rotuloDoAtendimento(at.org_nome, at.fila_nome)}`
  if (c.target_template_name) return `para ${c.target_template_name}`
  return 'sem time'
}

interface RemetenteDoChamadoProps {
  chamado: ChamadoDoRemetente
  /** O `StatusBadge` com o contexto de quem olha (decidido pela página). */
  status: React.ReactNode
  /** O indicador de SLA, quando a categoria tem SLA (ele consulta; este bloco não). */
  sla?: React.ReactNode
}

/**
 * O bloco do remetente do cartão de leitura (spec 2026-10-21 §3.2): é a ficha do chamado,
 * que antes ocupava uma coluna lateral. Três linhas: quem pediu e quando; de onde e para
 * quem; e as etiquetas.
 */
export function RemetenteDoChamado({ chamado: c, status, sla }: RemetenteDoChamadoProps) {
  const solicitante = c.lados?.solicitante
  const origem = solicitante
    ? `${solicitante.tenant_nome ?? `Tenant #${solicitante.tenant_id}`} · ${solicitante.org_nome ?? 'sem Organização'}`
    : null
  const desfecho = c.closed_at
    ? `Fechado em ${formatarData(c.closed_at)}`
    : c.resolved_at
      ? `Resolvido em ${formatarData(c.resolved_at)}`
      : idadeDe(c.created_at)

  return (
    <Group gap="md" align="flex-start" wrap="nowrap" data-testid="remetente-do-chamado">
      <Box
        aria-hidden
        visibleFrom="xs"
        style={{
          flex: '0 0 48px', width: 48, height: 48, borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: 'var(--ai-bg-subtle)',
          color: 'var(--ai-card-title, var(--ai-brand-azul))',
          fontWeight: 600,
        }}
      >
        {iniciaisDe(c.requested_by_name)}
      </Box>
      <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
        <Group justify="space-between" gap="xs" wrap="wrap">
          <Text fw={600} size="sm">
            {c.requested_by_name}
            {c.origin === 'ia_assistant' && (
              <Text span size="xs" fw={600} c="var(--ai-text-secondary)"> + @Atendente</Text>
            )}
          </Text>
          <Text size="xs" c="var(--ai-text-secondary)" ff="monospace">
            {formatarData(c.created_at)} · {desfecho}
          </Text>
        </Group>
        <Group justify="space-between" gap="xs" wrap="wrap">
          <Text size="sm" c="var(--ai-text-secondary)">
            {origem ? `${origem} — ` : ''}{destinoDoChamado(c)}
          </Text>
          {sla}
        </Group>
        <Group gap={6} wrap="wrap">
          <ProtocolNumberBadge value={c.protocol_number} withCopy />
          <Badge variant="outline" color="gray" size="sm">{rotuloDoTipoDeChamado(c.request_type)}</Badge>
          {c.category && (
            <Badge variant="outline" color="gray" size="sm">
              {c.category.name}
              {c.category.sla_resolution_hours ? ` · SLA ${c.category.sla_resolution_hours}h` : ''}
            </Badge>
          )}
          <PriorityBadge priority={c.priority} />
          {status}
          <Badge variant="outline" color="gray" size="sm">
            {c.assignee ? `Atendente: ${c.assignee.full_name}` : 'Sem atendente'}
          </Badge>
        </Group>
      </Stack>
    </Group>
  )
}
